/**
 * One thing on its own screen: the shell `UserDetailPage`, `GroupDetailPage`
 * and `RoleDetailPage` were each writing out by hand.
 *
 * Back link → title with its chips → description → the header actions → a
 * failure band → the body. That is the same forty lines three times, and the
 * differences between the copies were accidents: one page put its error above
 * the header and two put it below, one wrapped its title in a `Stack` for the
 * chips and one did not, so a chip appearing later would have to re-do it.
 *
 * ## What it is not
 *
 * **It does not fetch, and it does not know what an entity is** — the same rule
 * `EntityTable` follows, for the same reason. The page keeps its `useAsync` and
 * its `AsyncContent`, because "what happens while it loads" is a data question
 * and this file has no data.
 *
 * So there is no entity registry in here and no lookup by kind. It cannot be:
 * an action has to call `../api/` to do anything, and a component that reaches
 * `api/` — even transitively through a registry — is a component you cannot
 * render in a test without a server. The page resolves the entity's actions and
 * hands the rendered result over as `entity`; see
 * [`../pages/entities/README.md`].
 *
 * ```tsx
 * <AsyncContent state={role}>
 *   {({ role: record }) => (
 *     <EntityDetail
 *       backTo="/roles"
 *       backLabel={t('roleDetail.back')}
 *       title={record.name}
 *       badges={record.isBuiltin && <Chip size="small" label={t('roleDetail.builtin')} />}
 *       description={record.description ?? t('roleDetail.noDescription')}
 *       entity={actions.forRow(record)}
 *       actions={<Button …>{t('roleDetail.save')}</Button>}
 *       error={save.error}
 *     >
 *       …the permission editor and the assignee list…
 *     </EntityDetail>
 *   )}
 * </AsyncContent>
 * ```
 */
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import { ErrorState } from './ErrorState';
import { PageHeader } from './PageHeader';

/**
 * What can be done to this entity: the buttons, and the dialogs they open.
 *
 * Deliberately two **nodes** rather than an action registry. It is what keeps
 * the rule above true — this file renders them and never learns what any of
 * them costs. `useEntityActions` produces it, and the same declaration produces
 * the icons on the entity's table row, so the two screens offer the same set.
 */
export interface EntityAffordances {
  buttons: ReactNode;
  /**
   * Mounted by this component, at the end. A dialog is invisible until it
   * opens, which is exactly why a page that has to remember to render one
   * eventually ships a button that does nothing.
   */
  dialogs: ReactNode;
}

export interface EntityDetailProps {
  /** Where the back link goes — the list this record came from. */
  backTo: string;
  /** Its text. Required: an unlabelled back arrow is a guess for the reader. */
  backLabel: string;
  title: ReactNode;
  /**
   * Chips beside the title — "Built-in", "Deactivated", "You". Falsy entries
   * are fine, so a page writes `cond && <Chip/>` without a wrapper.
   */
  badges?: ReactNode;
  description?: ReactNode;
  /**
   * What can be done to the entity, from `useEntityActions` — the same set the
   * list row's icons offer. Rendered **first** in the action row, before this
   * screen's own buttons, because they belong to the record rather than to the
   * page showing it.
   */
  entity?: EntityAffordances | undefined;
  /**
   * Buttons belonging to this *screen* rather than to the entity: Save and
   * Discard over a draft that only exists while this page is open. They do not
   * mirror to a row, because a row has no draft.
   */
  actions?: ReactNode;
  /**
   * A failed write, shown above the body. Not a failed *load* — that one
   * belongs to `AsyncContent`, which renders instead of this whole screen.
   */
  error?: Error | undefined;
  /** Alerts between the header and the body: "unsaved changes", "built-in". */
  notices?: ReactNode;
  children: ReactNode;
}

export function EntityDetail(props: EntityDetailProps) {
  const {
    backTo,
    backLabel,
    title,
    badges,
    description,
    entity,
    actions,
    error,
    notices,
    children,
  } = props;

  const headerActions =
    entity?.buttons || actions ? (
      <>
        {entity?.buttons}
        {actions}
      </>
    ) : undefined;

  return (
    <>
      <PageHeader
        above={
          <Link component={RouterLink} to={backTo} underline="hover" variant="body2">
            {backLabel}
          </Link>
        }
        title={
          badges ? (
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <span>{title}</span>
              {badges}
            </Stack>
          ) : (
            title
          )
        }
        description={description}
        actions={headerActions}
      />

      {error && <ErrorState error={error} />}
      {notices}

      {children}

      {entity?.dialogs}
    </>
  );
}
